/**
 * What each channel did in the period, in that channel's own words — and never
 * in a shared one.
 *
 * **There is one component per channel and there will not be a parameterised
 * one.** `ChannelAnalytics` is a discriminated union on `channel` and both of its
 * branches name a field `notDelivered` whose types are unrelated, so
 * `analytics.notDelivered` on the un-narrowed union does not compile. That is
 * the compiler saying, in the only language it has, that there is no such thing
 * as a cross-channel failure figure — and these two functions are that statement
 * written out: each takes its own branch, and neither can be handed the other's.
 *
 * **There is no combined failure total anywhere on this screen.** Not a sum, not
 * a "problems" tile, not a badge counting both. A tenant reading "412 failed"
 * cleans their list; a tenant reading "1 unreachable number, 411 rate-limited,
 * 3 hard bounces" sends again tomorrow and keeps 411 customers. Amazon reports
 * five different not-delivered events with five different owners; Meta reports
 * one word, `failed`, with an error code that is the entire diagnosis. Those are
 * not two spellings of one measurement, and the contract enforces it —
 * `CROSS_CHANNEL_MEASURE_VALUES` has no `'failed'` member, so a combined failure
 * total is neither constructible nor parseable.
 *
 * **The outcome rows are borrowed; the container is not.** `NOT_DELIVERED_OUTCOMES`
 * and `WA_FAILURE_ROWS` — the labels, the tones, the order, and on the email side
 * the "what can you do about it" sentence — come from the campaign panels, so
 * this screen and the campaign screen cannot describe a soft bounce or a pair
 * rate limit differently. What is *not* reused is those components themselves:
 * their empty states are written about one campaign ("WhatsApp has not reported
 * a failure on this campaign") and this is a period holding many, so rendering
 * them here would put a sentence on screen that is about the wrong thing. One
 * vocabulary, two containers, and the alternative — a second set of outcome
 * sentences owned by this file — is how two screens start disagreeing about what
 * a transient failure means.
 *
 * **Every count here is a count of distinct messages, and the screen says so.**
 * Where each one is counted from differs, and the contract on
 * `emailAnalyticsSchema` is the authority: anything read from `delivery_events`
 * — the complaints, the bounce split, the not-delivered breakdowns — is
 * `countDistinct(message_id)` and never `count(*)`, because SES, SNS and Meta
 * all re-notify and slices 10, 11 and 12 each shipped a version of the resulting
 * double count; while `accepted`, `delivered` and `failed` are read from
 * `messages`, one row per message, because a message can carry both a delivery
 * event and a later failure and counting those from events puts it in two
 * mutually exclusive buckets. The API owns both queries; what this file owes the
 * reader is the sentence saying which of the two things was counted, because the
 * two are indistinguishable once they are digits on a screen.
 *
 * **The breakdowns do not add up to the counters above them, deliberately.** A
 * message can honestly be in two rows — delayed for an hour and then hard
 * bounced, or refused twice for two different reasons — so neither breakdown has
 * a total, and neither reconciles against `delivered` or `failed`. The screen
 * says that too, rather than leaving a reader to discover it by subtracting.
 *
 * **No rate is drawn on this screen, and the omission is deliberate rather than
 * a gap.** `analyticsReportSchema` carries counts and no `RateReading`, which is
 * the contract refusing the thing slice 10 shipped as a critical: bounce and
 * complaint rates are measured against thresholds AWS enforces on *email*, and a
 * rate computed in a browser is a second implementation with no volume floor
 * under it. A percentage over a hundred and forty messages moves by whole points
 * on a single bad address. The rates live on the Overview, email-only, drawn
 * against the AWS line and withheld below the floor; this screen counts.
 *
 * **A channel that sent nothing in the period is not a channel that failed, and
 * the two produce the same zeros.** `accepted === 0` with `recipientRows === 0`
 * means no campaign on this channel went out at all; `accepted === 0` with rows
 * materialised means a send happened and the provider took none of it — usually
 * the send guard — which is a real and alarming measurement. They are told apart
 * by the row count and given different sentences, because a column of noughts
 * under "Delivered" reads as the second when it is nearly always the first. This
 * is the same zero-versus-absent rule the nullable channels carry, one level
 * down: `null` is "not sent from here ever", and this is "not sent here in these
 * dates".
 *
 * The one figure here that *is* a proportion is the WhatsApp read floor, and it
 * arrives as a `WaReadRate` — a floor, not a rate, with `confidence: 'floor'`,
 * no threshold and no band — so it cannot reach a component that draws a verdict.
 * See {@link AnalyticsWaReadFloor}.
 *
 * **Slice 17 shortened every paragraph on this panel and removed none of the
 * distinctions.** The rule it applied is the one that sorts the text here into
 * two piles. *Accepted is not delivered*, *hard is not soft*, *these do not
 * total*, *the rates are on the Overview*, *not enough volume yet*, *this is a
 * floor and not a rate*, *there is no email counterpart* — every one of those is
 * a statement a reader draws a false conclusion without, and every one of them
 * is still on the screen, in fewer words and never behind a disclosure. What
 * went is the mechanism behind them: that SES and SNS both re-notify and a
 * redelivery arrives under a fresh id, that a hard-bounced address left on a
 * list is what puts an AWS account under review. Those explain *why* the figure
 * is built the way it is, they are in this header, and a person reading a
 * delivered count does not need them to read it correctly.
 *
 * The one sentence that went without a shortened replacement was "this is the
 * status that spends a credit", on the WhatsApp delivered counter. It was true,
 * and it was the only credit sentence on either channel — email's delivered
 * counter never carried it — so it read as a difference between the channels
 * where there is none. What a delivery does to a balance is `/credits`'s subject
 * and is stated there on every row of the ledger.
 */
import {
  WA_FAILURE_CLASS_MESSAGES,
  WA_READ_CAVEAT_MESSAGES,
  type EmailAnalytics,
  type WaReadRate,
  type WhatsAppAnalytics,
} from '@wend/shared';
import { Badge, Kicker, cn, type BadgeTone } from '@wend/ui';

import { formatCount, pluralise } from '../../lib/format';
import { NOT_DELIVERED_OUTCOMES } from '../campaigns/CampaignNotDelivered';
import { WA_FAILURE_ROWS } from '../campaigns/CampaignWaFailures';

/* -------------------------------------------------------------------------- */
/* Building blocks                                                             */
/* -------------------------------------------------------------------------- */

/**
 * One count with the sentence that says what it counted.
 *
 * The caption is not decoration and it is not optional: `accepted` and
 * `delivered` are two different events, and a column of bare numbers under bare
 * nouns is how the two get read as one.
 *
 * Slice 66 drew each count as the Blueprint's metric tile — the kicker role,
 * a sans figure with tabular digits (mono's dotted zero reads as an 8), and the
 * caption under it, in a hairline box — rather than as the shared
 * `MetricPanel`. The panel paints its figure in one colour, always, because a
 * panel's figure is magnitude; these counters carry a verdict tone — a
 * non-zero complaint or hard bounce is red, a soft bounce amber — and that
 * tone is a status the reader needs, so it stays on the number.
 */
function Counter({
  label,
  value,
  caption,
  tone,
}: {
  label: string;
  value: number;
  caption: string;
  tone?: string;
}) {
  return (
    <div className="flex min-w-[10rem] flex-1 flex-col gap-1.5 rounded-base border border-line bg-white/[0.04] p-3.5">
      <Kicker as="dt">{label}</Kicker>
      <dd className="flex flex-col gap-1">
        <span
          className={cn(
            'text-[1.5rem] font-semibold leading-tight tracking-[-0.03em] tabular-nums',
            tone ?? 'text-text',
          )}
        >
          {formatCount(value)}
        </span>
        <span className="text-[0.8rem] leading-snug text-muted">{caption}</span>
      </dd>
    </div>
  );
}

/** One breakdown row: the count, the chip, and what the reader can do about it. */
function OutcomeRow({
  count,
  label,
  tone,
  sentence,
}: {
  count: number;
  label: string;
  tone: BadgeTone;
  sentence: string;
}) {
  return (
    <li className="flex flex-col gap-2 border-t border-line py-3.5 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[1.05rem] font-semibold tabular-nums text-text">
          {formatCount(count)}
        </span>
        <Badge tone={tone}>{label}</Badge>
      </div>
      <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">{sentence}</p>
    </li>
  );
}

function SectionHeading({ children }: { children: string }) {
  return (
    <Kicker rule>{children}</Kicker>
  );
}

/* -------------------------------------------------------------------------- */
/* Email                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Amazon's vocabulary: accepted, delivered, complained, bounced hard or soft,
 * and the five ways a message it took never arrived.
 *
 * `bounces` and `notDelivered` overlap on purpose and both are shown, exactly as
 * they are on a campaign's results: one splits the bounced *messages* hard from
 * soft, the other splits the *events* that stopped a delivery, and they disagree
 * in the informative case — a rejection or a rendering failure never became a
 * bounce at all. Neither has a total and nothing here computes one.
 */
export function AnalyticsEmailOutcomes({ analytics }: { analytics: EmailAnalytics }) {
  const failureRows = NOT_DELIVERED_OUTCOMES.filter(
    (outcome) => analytics.notDelivered[outcome.key] > 0,
  );
  // No rows materialised and nothing accepted: no email campaign went out at all.
  // Rows with nothing accepted is a different fact and keeps its zeros.
  const nothingSent = analytics.recipientRows.rows === 0 && analytics.accepted === 0;

  return (
    <div className="flex flex-col gap-5">
      {nothingSent && (
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          No email campaign went out in this period. The zeros below are the absence of sends, not
          a measurement of sends that failed.
        </p>
      )}

      <dl className="flex flex-wrap gap-2.5">
        <Counter
          label="Accepted"
          value={analytics.accepted}
          caption="Distinct messages Amazon took. Taking one is not delivering it."
        />
        <Counter
          label="Delivered"
          value={analytics.delivered}
          caption="Distinct messages Amazon reported as reaching the receiving mail server."
        />
        <Counter
          label="Complaints"
          value={analytics.complaints}
          caption="Distinct messages somebody marked as spam."
          tone={analytics.complaints > 0 ? 'text-bad' : undefined}
        />
      </dl>

      <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
        Every figure counts messages, not the notifications Amazon sent about them.
      </p>

      <div className="flex flex-col gap-3 border-t border-line pt-5">
        <SectionHeading>Bounces</SectionHeading>

        <dl className="flex flex-wrap gap-2.5">
          <Counter
            label="Hard"
            value={analytics.bounces.hard}
            caption="Address gone or permanently refused. Already suppressed; repair the list."
            tone={analytics.bounces.hard > 0 ? 'text-bad' : undefined}
          />
          <Counter
            label="Soft"
            value={analytics.bounces.soft}
            caption="Full, greylisted or throttled. Nobody suppressed — worth sending to again."
            tone={analytics.bounces.soft > 0 ? 'text-warn' : undefined}
          />
          <Counter
            label="Undetermined"
            value={analytics.bounces.undetermined}
            caption="Amazon would not say which. Neither of the two above, and nobody suppressed."
          />
        </dl>

        <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
          Kept apart rather than added up: deleting a soft-bounced address is the expensive mistake.
          Bounce and complaint <span className="text-text">rates</span> are not drawn here — they
          are measured against Amazon&rsquo;s thresholds, over email only, and are on the Overview.
        </p>
      </div>

      <div className="flex flex-col gap-3 border-t border-line pt-5">
        <SectionHeading>Why messages did not arrive</SectionHeading>

        {failureRows.length === 0 ? (
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            Nothing has come back from Amazon saying a message in this period failed to arrive — no
            rejection, no bounce, no delay and no rendering failure. That is a read of the events on
            file, not a promise about messages still awaiting an answer.
          </p>
        ) : (
          <>
            <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
              Five outcomes, kept apart because they need different responses. They do not total and
              do not reconcile against the counters above — a message delayed and then bounced is
              honestly on two lines.
            </p>

            <ul className="flex flex-col">
              {failureRows.map((outcome) => (
                <OutcomeRow
                  key={outcome.key}
                  count={analytics.notDelivered[outcome.key]}
                  label={outcome.label}
                  tone={outcome.tone}
                  sentence={outcome.sentence}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* WhatsApp                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Meta's vocabulary: accepted, delivered, read, failed — and the seven classes
 * behind that one word.
 *
 * There is no bounce here and there never will be. Meta reports every
 * non-delivery as `failed` with an error code, so an unregistered number, a
 * blown rate limit and a paused template all arrive as the same word;
 * `classifyWaFailureCode` in the contract owns the 38-code table that separates
 * them, and nothing in this file performs that classification.
 */
export function AnalyticsWhatsAppOutcomes({ analytics }: { analytics: WhatsAppAnalytics }) {
  const failureRows = WA_FAILURE_ROWS.filter((row) => analytics.notDelivered[row.key] > 0);
  const nothingSent = analytics.recipientRows.rows === 0 && analytics.accepted === 0;

  return (
    <div className="flex flex-col gap-5">
      {nothingSent && (
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          No WhatsApp campaign went out in this period. The zeros below are the absence of sends,
          not a measurement of sends that failed.
        </p>
      )}

      <dl className="flex flex-wrap gap-2.5">
        <Counter
          label="Accepted"
          value={analytics.accepted}
          caption="Distinct messages the Graph API took. Taking one is not delivering it."
        />
        <Counter
          label="Delivered"
          value={analytics.delivered}
          caption="Distinct messages WhatsApp reported as reaching a device."
        />
        <Counter
          label="Failed"
          value={analytics.failed}
          caption="Distinct messages WhatsApp reported as failed. One word for seven things — see below."
          tone={analytics.failed > 0 ? 'text-bad' : undefined}
        />
      </dl>

      <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
        Every figure counts messages, not the status notifications Meta sent about them.
      </p>

      <div className="flex flex-col gap-3 border-t border-line pt-5">
        <SectionHeading>Why messages did not arrive</SectionHeading>

        {failureRows.length === 0 ? (
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            {analytics.failed === 0
              ? 'WhatsApp has not reported a failure in this period. That is a read of the statuses on file, not a promise about messages still waiting for one.'
              : 'WhatsApp reported failures in this period but sent no reason Wend could read with them, so there is nothing to break down. Nobody has been suppressed on the strength of it.'}
          </p>
        ) : (
          <>
            <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
              WhatsApp reports every one of these as the single word &ldquo;failed&rdquo;. Kept apart
              because only some are worth changing a contact list over. They do not total: one
              message refused twice is honestly on two lines.
            </p>

            <ul className="flex flex-col">
              {failureRows.map((row) => (
                <OutcomeRow
                  key={row.key}
                  count={analytics.notDelivered[row.key]}
                  label={row.label}
                  tone={row.tone}
                  sentence={WA_FAILURE_CLASS_MESSAGES[row.key]}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      <AnalyticsWaReadFloor
        read={analytics.read}
        delivered={analytics.delivered}
        rate={analytics.readRate}
      />
    </div>
  );
}

/**
 * The read figure, drawn so it cannot be mistaken for a readership rate.
 *
 * The same treatment `CampaignWhatsAppDelivery` gives it one screen over, and
 * for the same reason: anybody can switch read receipts off, Meta then reports
 * no `read` status for them ever, and they are missing from the numerator with
 * nothing anywhere saying how many of them there were. So the count leads, the
 * percentage is second and hedged, there is no colour, no threshold and no
 * verdict, and the two caveats are printed at the number rather than under the
 * page — a caveat a reader has to scroll to find is a caveat that was not made.
 * Both caveat sentences are the contract's `WA_READ_CAVEAT_MESSAGES`, so this
 * screen and the campaign screen cannot hedge the same number differently.
 *
 * It takes the whole {@link WaReadRate} rather than a number: the object travels
 * with its caveats attached, because a rate separated from its hedge is an exact
 * rate as far as the reader is concerned.
 *
 * `read` and `delivered` are counted over different populations — `read` over
 * every message, `delivered` over the ones that reached a device — so `read` can
 * exceed `delivered` when Meta has not reported all of the deliveries. That is
 * not a bug and it is not papered over: the reads are real and are shown as a
 * count, and the ratio is withheld, because "at least 100%" over a denominator
 * that is missing rows is a false claim rather than a rounded one.
 */
function AnalyticsWaReadFloor({
  read,
  delivered,
  rate,
}: {
  read: number;
  delivered: number;
  rate: WaReadRate;
}) {
  const { reading } = rate;
  const nothingMeasured = delivered === 0 && read === 0;
  const denominatorIncomplete = read > delivered;

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-5">
      <SectionHeading>Marked read</SectionHeading>

      {nothingMeasured ? (
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          Nothing was delivered on WhatsApp in this period and no read receipt has come back, so
          there is nothing to report here.
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          <p className="text-[1.35rem] font-semibold leading-none tabular-nums text-text">
            {formatCount(read)}
            <span className="text-[0.9rem] text-muted">
              {denominatorIncomplete
                ? ' marked read'
                : ` of ${pluralise(delivered, 'delivered message')}`}
            </span>
          </p>

          {denominatorIncomplete ? (
            <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
              Shown as a count on its own: WhatsApp has reported fewer deliveries than reads in this
              period, so the reads are real but a percentage over an incomplete denominator would
              not be.
            </p>
          ) : reading.rate === null ? (
            <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
              Not enough volume yet — shown as counts rather than a percentage. Under{' '}
              {formatCount(reading.minimumVolume)} delivered messages a read figure moves several
              points per person.
            </p>
          ) : (
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-[1.5rem] font-semibold leading-none tabular-nums text-text">
                at least {(reading.rate * 100).toFixed(1)}%
              </span>
              <Badge tone="neutral">Floor, not a rate</Badge>
            </div>
          )}

          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            Not the share of people who read these messages, and it cannot be turned into one:
            WhatsApp reports a read only for recipients who left read receipts on, and everybody who
            turned them off can never appear in it. There is no email counterpart on this screen —
            the two are broken in different directions and are not comparable.
          </p>

          <ul className="flex flex-col gap-1.5">
            {rate.caveats.map((caveat) => (
              <li key={caveat} className="text-[0.78rem] leading-relaxed text-muted">
                {WA_READ_CAVEAT_MESSAGES[caveat]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
